"use client";

import { useLocale } from "@calcom/lib/hooks/useLocale";
import { ArrowLeft, CalendarSearch, Search } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import type { JSX } from "react";
import { useLayoutEffect } from "react";

type NotFoundProps = {
  host: string;
};

const AUTO_PRIMARY = "#075F78";
const AUTO_PRIMARY_DARK = "#063D56";
const AUTO_PRIMARY_DEEP = "#042D40";

function BackgroundDecoration(): JSX.Element {
  return (
    <>
      <div
        style={{
          position: "absolute",
          top: "-160px",
          right: "-140px",
          width: "560px",
          height: "560px",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 62%)",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: "-260px",
          left: "-140px",
          width: "720px",
          height: "720px",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(0,0,0,0.07) 0%, transparent 64%)",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: "radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)",
          backgroundSize: "28px 28px",
          pointerEvents: "none",
          maskImage: "radial-gradient(ellipse 80% 65% at 50% 50%, black 30%, transparent 100%)",
          WebkitMaskImage: "radial-gradient(ellipse 80% 65% at 50% 50%, black 30%, transparent 100%)",
        }}
      />
    </>
  );
}

function ContentPanel({
  description,
  isBookingPage,
  title,
}: {
  description: string;
  isBookingPage: boolean;
  title: string;
}): JSX.Element {
  const { t } = useLocale();
  let icon = <Search size={22} />;
  if (isBookingPage) icon = <CalendarSearch size={22} />;

  return (
    <div
      style={{
        backgroundColor: "#ffffff",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        padding: "56px 44px",
        position: "relative",
        minHeight: "520px",
      }}>
      <div className="mb-8" style={{ textAlign: "center" }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: "10px" }}>
          <Image
            src="/trafega-auto-logo.svg"
            alt="Trafega Pro"
            width={120}
            height={42}
            style={{ height: "34px", width: "auto", objectFit: "contain" }}
          />
        </div>
      </div>

      <div
        style={{
          width: "100%",
          maxWidth: "440px",
          margin: "0 auto",
          borderRadius: "24px",
          border: "1px solid #e6ebf0",
          backgroundColor: "#ffffff",
          padding: "40px 36px",
          boxShadow: "0 20px 50px rgba(15,23,42,0.08)",
          textAlign: "center",
        }}>
        <div
          style={{
            width: "56px",
            height: "56px",
            borderRadius: "999px",
            border: "2px solid #e6ebf0",
            backgroundColor: "#f8fbf9",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 18px",
            color: AUTO_PRIMARY,
          }}>
          {icon}
        </div>

        <span
          style={{
            display: "inline-block",
            padding: "5px 12px",
            borderRadius: "999px",
            backgroundColor: "#f3f5f8",
            fontSize: "12px",
            fontWeight: 600,
            color: "#6b7280",
            letterSpacing: "0.05em",
            marginBottom: "14px",
            textTransform: "uppercase",
          }}>
          {t("error_404")}
        </span>

        <h1
          style={{
            fontSize: "22px",
            fontWeight: 700,
            color: "#111827",
            marginBottom: "8px",
            letterSpacing: "-0.02em",
          }}>
          {title}
        </h1>

        <p style={{ fontSize: "15px", color: "#98a2b3", lineHeight: 1.65, marginBottom: "28px" }}>
          {description}
        </p>

        <Link
          href="/"
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            padding: "13px 28px",
            background: `linear-gradient(90deg, ${AUTO_PRIMARY} 0%, ${AUTO_PRIMARY_DARK} 100%)`,
            color: "#ffffff",
            border: "none",
            borderRadius: "12px",
            fontSize: "14px",
            fontWeight: 600,
            textDecoration: "none",
            boxShadow: "0 10px 24px rgba(7,95,120,0.26)",
          }}>
          <ArrowLeft size={16} />
          {t("go_back_home")}
        </Link>
      </div>
    </div>
  );
}

export function NotFound({ host: _host }: NotFoundProps): JSX.Element {
  const pathname = usePathname() ?? "";
  const isBookingPage = pathname.startsWith("/booking");
  const { t } = useLocale();

  useLayoutEffect(() => {
    if (typeof window !== "undefined") {
      window.CalComPageStatus = "404";
    }
  }, []);

  let title = t("404_not_found_title");
  let description = t("404_not_found_description");

  if (isBookingPage) {
    title = t("404_booking_not_found_title");
    description = t("404_booking_not_found_description");
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: `linear-gradient(145deg, ${AUTO_PRIMARY_DEEP} 0%, ${AUTO_PRIMARY} 48%, ${AUTO_PRIMARY_DARK} 100%)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
        fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
        position: "relative",
        overflow: "hidden",
      }}
      data-testid="404-page">
      <BackgroundDecoration />

      <div
        style={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          maxWidth: "560px",
          borderRadius: "30px",
          overflow: "hidden",
          boxShadow:
            "0 0 0 1px rgba(255,255,255,0.07), 0 20px 80px rgba(4,45,64,0.28), 0 32px 120px rgba(0,0,0,0.16)",
          minHeight: "520px",
        }}>
        <ContentPanel description={description} isBookingPage={isBookingPage} title={title} />
      </div>
    </div>
  );
}
